import { isEmpty, isNil, toString } from 'lodash';
import { MarketComparisonWritable } from 'api';
import { DECIMAL_PLACES } from 'common/config/app';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_COMPARISON,
  FUND_INVESTMENTS_PUBLIC_COMPS_INDEX_TYPES,
} from 'pages/FundInvestments/common/constants';
import { ParsedPublicCompsValues, parsePublicCompsValues } from 'pages/FundInvestments/utils/parsePublicCompsValues';
import { getObjectValue, parseFieldValue, ParseFieldValueParams } from 'utilities';
import {
  MarketComparisonFieldAttributesKeys,
  ParseField,
  ParsePublicCompsMarketComparisonParams,
} from './parsePublicCompsMarketComparison.types';

const parsePublicCompsMarketComparison = (
  params: ParsePublicCompsMarketComparisonParams
): MarketComparisonWritable | null => {
  const { fieldAttributes, order, publicComp } = params;

  if (isNil(publicComp) || isEmpty(publicComp) || isNil(fieldAttributes) || isEmpty(fieldAttributes)) {
    return null;
  }

  const parsedPublicCompValues = parsePublicCompsValues(publicComp);

  const parseField: ParseField = fieldKey => {
    const { decimal_places: decimalPlaces, type } = getObjectValue(
      fieldAttributes[fieldKey as MarketComparisonFieldAttributesKeys]
    );

    const safeDecimals = typeof decimalPlaces === 'number' ? decimalPlaces : DECIMAL_PLACES;

    return parseFieldValue({
      value: parsedPublicCompValues[fieldKey],
      type: type as ParseFieldValueParams['type'],
      decimalPlaces: safeDecimals,
    }) as ParsedPublicCompsValues[typeof fieldKey];
  };

  const isIndex = FUND_INVESTMENTS_PUBLIC_COMPS_INDEX_TYPES.includes(toString(parseField('company_type')));

  return {
    ...FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_COMPARISON,
    as_of_date: parseField('as_of_date'),
    business_description: parseField('business_description'),
    cap_iq_id: parseField('cap_iq_id'),
    close_price: parseField('close_price'),
    company_name: parseField('company_name'),
    company_type: parseField('company_type'),
    currency: parseField('currency'),
    enterprise_value_excluding_operating_leases: parseField('ev_excl_operating_leases'),
    enterprise_value: parseField('enterprise_value'),
    forward_net_income: parseField('forward_net_income'),
    is_index: isIndex,
    ltm_ebitda: parseField('ltm_ebitda'),
    ltm_revenue_growth: parseField('ltm_revenue_growth'),
    ltm_revenue: parseField('ltm_revenue'),
    market_cap: parseField('market_cap'),
    net_income_to_common_excl_extra_items: parseField('net_income_to_common_excl_extra_items'),
    net_income: parseField('net_income'),
    ntm_ebitda: parseField('ntm_ebitda'),
    ntm_revenue_growth: parseField('ntm_revenue_growth'),
    ntm_revenue: parseField('ntm_revenue'),
    order: order ?? FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_COMPARISON.order,
    tangible_book_value_estimate: parseField('tangible_book_value_estimate'),
    tangible_book_value: parseField('tangible_book_value'),
    ticker_symbol: parseField('ticker_symbol'),
  } as MarketComparisonWritable;
};

export default parsePublicCompsMarketComparison;
